/**
 * Two approved revisions of one programme, compared.
 *
 * Presentational: which revisions, and the diff of them, are given. The five
 * views are tabs over one already-loaded comparison, so switching between
 * them costs nothing and never reloads.
 *
 * @module components/portfolio/comparison/RevisionComparison
 */

import React, { useEffect, useRef, useState } from "react";

import { ChangeRegister } from "@/components/portfolio/comparison/ChangeRegister";
import type { ComparisonArea } from "@/components/portfolio/comparison/comparison-areas";
import { buildComparisonModel } from "@/components/portfolio/comparison/comparison-model";
import { ComparisonOverview } from "@/components/portfolio/comparison/ComparisonOverview";
import { ComparisonSummary } from "@/components/portfolio/comparison/ComparisonSummary";
import { MappingChangeList } from "@/components/portfolio/comparison/MappingChangeList";
import { ModuleChangeList } from "@/components/portfolio/comparison/ModuleChangeList";
import {
  ComparisonScopeBar,
  RevisionPicker,
} from "@/components/portfolio/comparison/RevisionPicker";
import { RevisionSideBySide } from "@/components/portfolio/comparison/RevisionSideBySide";
import {
  Button,
  ErrorAlert,
  Eyebrow,
  Heading,
  Icon,
  MutedText,
  type TabItem,
  TabPanel,
  Tabs,
} from "@/components/ui";
import type { RevisionComparison as RevisionComparisonResult } from "@/server/governance-diff";
import type { CurriculumRevision } from "@/server/types";

import { ComparisonLoading } from "./ComparisonLoading";

type ComparisonView = "overview" | "modules" | "mappings" | "details" | "side-by-side";

/** Said once, in the region and beside the spinner. */
const COMPARING_LABEL = "Comparing curriculum revisions";

const VIEW_OPTIONS: readonly (TabItem & { key: ComparisonView })[] = [
  {
    key: "overview",
    label: "Overview",
    icon: "chart-bar",
    "data-testid": "comparison-view-overview",
  },
  { key: "modules", label: "Modules", icon: "books", "data-testid": "comparison-view-modules" },
  {
    key: "mappings",
    label: "Mapping",
    icon: "share-network",
    "data-testid": "comparison-view-mappings",
  },
  {
    key: "details",
    label: "All changes",
    icon: "list-bullets",
    "data-testid": "comparison-view-details",
  },
  {
    key: "side-by-side",
    label: "Side-by-side",
    icon: "columns",
    "data-testid": "comparison-view-side-by-side",
  },
];

export interface RevisionComparisonProps {
  /** Every approved revision of this programme, earliest first. */
  revisions: readonly CurriculumRevision[];
  fromRevisionId: string;
  toRevisionId: string;
  onFromChange: (revisionId: string) => void;
  onToChange: (revisionId: string) => void;
  /** The diff of the chosen pair, absent while it is still being read. */
  comparison: RevisionComparisonResult | null;
  comparing: boolean;
  /** Shown under the picker, so a failed compare keeps the picker usable. */
  errorMessage?: string;
  onBack: () => void;
  /** Names the programme when neither revision is on screen yet. */
  fallbackTitle: string;
}

/**
 * @example
 * ```tsx
 * <RevisionComparison revisions={ordered} fromRevisionId={from} toRevisionId={to} … />
 * ```
 */
export function RevisionComparison({
  revisions,
  fromRevisionId,
  toRevisionId,
  onFromChange,
  onToChange,
  comparison,
  comparing,
  errorMessage = undefined,
  onBack,
  fallbackTitle,
}: RevisionComparisonProps): React.ReactElement {
  const [view, setView] = useState<ComparisonView>("overview");
  const [changeCategory, setChangeCategory] = useState<ComparisonArea>("all");
  // Beside the area, not inside the register: the register unmounts with its
  // tab, so a search held there would reset on a tab round-trip while the area
  // survived, and the reader would get half their filters back.
  const [changeSearch, setChangeSearch] = useState("");
  // Bumped when the summary asks for the impact panel, which then scrolls
  // itself. A counter, not a boolean, so asking twice scrolls twice.
  const [impactFocus, setImpactFocus] = useState(0);

  const fromRevision = revisions.find((revision) => revision.id === fromRevisionId);
  const toRevision = revisions.find((revision) => revision.id === toRevisionId);
  // A diff is only shown against the pair it was taken of. Changing a select
  // re-renders before the read has reset to loading, so the loaded diff still
  // describes the pair just left, and rendering it paints one revision's
  // documents under another pair's numbers.
  const diff =
    comparison?.fromRevisionId === fromRevisionId && comparison.toRevisionId === toRevisionId
      ? comparison
      : null;
  // `comparing` lags a commit behind: the read moves to loading inside an
  // effect, so the render that rejects the stale diff still sees it as ready.
  // Derived, the wait covers that frame instead of the screen going blank.
  // Not the error path, which has no pair attached to check: a failed compare
  // keeps its alert for that one commit rather than being told apart.
  const busy =
    comparing ||
    (Boolean(fromRevision && toRevision) &&
      fromRevisionId !== toRevisionId &&
      diff === null &&
      errorMessage === undefined);
  const model = buildComparisonModel(fromRevision, toRevision, diff);

  const waitRegion = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Written imperatively rather than through state, which
    // `react-hooks/set-state-in-effect` forbids: the announcement is the side
    // effect, not the render.
    if (waitRegion.current) {
      waitRegion.current.textContent = busy ? COMPARING_LABEL : "";
    }
  }, [busy]);

  // The impact panel unmounts with the overview tab, so a token left standing
  // would scroll again on every return to it. Leaving the overview clears the
  // request rather than the panel remembering it across an unmount.
  const openView = (next: ComparisonView): void => {
    setView(next);
    if (next !== "overview") {
      setImpactFocus(0);
    }
  };

  const openRegister = (area: ComparisonArea): void => {
    setChangeCategory(area);
    openView("details");
  };

  return (
    <div className="space-y-7" data-testid="revision-comparison-workspace">
      <header>
        <Button variant="ghost" size="sm" onClick={onBack} icon="arrow-left">
          Back to Portfolio
        </Button>
        <Eyebrow className="mt-4 flex items-center gap-2 text-primary">
          <Icon name="git-diff" aria-hidden />
          Approved programme history
        </Eyebrow>
        <Heading level={1} className="mt-1">
          Compare curriculum revisions
        </Heading>
        <MutedText className="mt-1">
          {toRevision?.data.title ?? fromRevision?.data.title ?? fallbackTitle}
        </MutedText>
      </header>

      <RevisionPicker
        revisions={revisions}
        fromRevisionId={fromRevisionId}
        toRevisionId={toRevisionId}
        onFromChange={onFromChange}
        onToChange={onToChange}
      />

      {fromRevision && toRevision ? (
        <ComparisonScopeBar fromRevision={fromRevision} toRevision={toRevision} />
      ) : null}

      {errorMessage ? (
        // Its own id: `revision-comparison-error` is the route's, for the
        // screen refusing to load, and one id naming two failures cannot say
        // which read broke.
        <ErrorAlert data-testid="comparison-compare-error">{errorMessage}</ErrorAlert>
      ) : null}
      {/* Empty in the markup and filled a tick later, the way `AsyncBoundary`
          does it. The route only ever mounts this screen with the compare
          already in flight, so a region rendered with its text would arrive
          holding it — which a screen reader watching for a change misses. */}
      <div ref={waitRegion} role="status" aria-live="polite" className="sr-only" />
      {busy ? <ComparisonLoading label={COMPARING_LABEL} /> : null}

      {diff && !busy ? (
        <>
          <ComparisonSummary
            metrics={[
              {
                key: "changes",
                label: "Changes recorded",
                value: model.substantiveChanges.length,
                onOpen: () => {
                  openRegister("all");
                },
              },
              {
                key: "modules",
                label: "Modules changed",
                value: model.changedModules.length,
                onOpen: () => openView("modules"),
              },
              {
                key: "mappings",
                label: "Mapping links changed",
                value: model.mappings.length,
                onOpen: () => openView("mappings"),
              },
              {
                key: "outputs",
                label: "Outputs affected",
                value: diff.impact.affectedDerivedOutputs.length,
                onOpen: () => {
                  setView("overview");
                  setImpactFocus((token) => token + 1);
                },
              },
            ]}
          />

          <Tabs
            variant="box"
            items={VIEW_OPTIONS}
            value={view}
            onChange={(key) => openView(key as ComparisonView)}
            aria-label="Comparison views"
            idPrefix="comparison"
          />

          {/* One panel per tab, not one for the view on screen: a tab whose
              `aria-controls` names an id that is not on the page sends a
              reader nowhere. `TabPanel` drops the children of an inactive
              panel, so only the open view is ever mounted. */}
          {VIEW_OPTIONS.map(({ key }) => (
            <TabPanel key={key} tabKey={key} idPrefix="comparison" active={view === key}>
              {key === "overview" ? (
                <ComparisonOverview
                  areas={model.areas}
                  totalChanges={model.substantiveChanges.length}
                  onOpenArea={openRegister}
                  fromRevision={fromRevision}
                  toRevision={toRevision}
                  moduleDifferences={model.modules}
                  changedModuleCount={model.changedModules.length}
                  outcomeChangeCount={
                    model.areas.find((area) => area.id === "programme-outcomes")?.count ?? 0
                  }
                  earlierAssessmentProfile={model.earlierAssessmentProfile}
                  laterAssessmentProfile={model.laterAssessmentProfile}
                  identityChanges={model.identityChanges}
                  affectedOutputs={diff.impact.affectedDerivedOutputs}
                  impactFocus={impactFocus}
                />
              ) : null}
              {key === "modules" ? (
                <ModuleChangeList
                  modules={model.modules}
                  projectedChanges={model.projectedChangesById}
                />
              ) : null}
              {key === "mappings" ? <MappingChangeList mappings={model.mappings} /> : null}
              {key === "details" ? (
                <ChangeRegister
                  changes={model.substantiveChanges}
                  technicalChanges={model.technicalChanges}
                  category={changeCategory}
                  onCategoryChange={setChangeCategory}
                  search={changeSearch}
                  onSearchChange={setChangeSearch}
                />
              ) : null}
              {key === "side-by-side" && fromRevision && toRevision ? (
                <RevisionSideBySide fromRevision={fromRevision} toRevision={toRevision} />
              ) : null}
            </TabPanel>
          ))}
        </>
      ) : null}
    </div>
  );
}
